import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  fetchHeroProfile,
  getUserDisplayName,
  useCurrentUser,
  type HeroProfile,
} from "@/lib/auth";
import { devSimulatedBirthDateToday, isMyBirthdayToday } from "@/lib/birthday";
import { fetchTodaysBirthdays, type HeroEmployeeBirthday } from "@/lib/home";
import { isChristmasSeason, isHalloweenSeason } from "@/lib/season";
import {
  decoAllowsBirthdayConfetti,
  decoAllowsChristmasDecor,
  decoAllowsChristmasSnow,
  decoAllowsHalloweenBats,
  decoAllowsHalloweenFullDecor,
} from "@/lib/decoPreference";
import { useDecoPreference } from "@/features/deco/DecoPreferenceProvider";
import { useVacation } from "@/features/vacation/VacationProvider";
import { useBirthdayConfetti } from "@/hooks/useBirthdayConfetti";
import { useChristmasEffects } from "@/hooks/useChristmasEffects";
import { useHalloweenEffects } from "@/hooks/useHalloweenEffects";

export interface BirthdayData {
  me: HeroProfile | null;
  todays: HeroEmployeeBirthday[];
}

interface BirthdayContextValue {
  /** Sofort nutzbare Daten (optimistisch, falls voller Fetch noch läuft). */
  data: BirthdayData | null;
  mineIsBirthday: boolean;
  loading: boolean;
  reload: () => Promise<void>;
}

const BirthdayContext = createContext<BirthdayContextValue | null>(null);

export function BirthdayProvider({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser();
  const { vacationActive } = useVacation();
  const { preference: decoPreference } = useDecoPreference();
  const [data, setData] = useState<BirthdayData | null>(null);
  const [heroProfile, setHeroProfile] = useState<HeroProfile | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!user) {
      setData(null);
      setHeroProfile(null);
      return;
    }

    setLoading(true);
    try {
      const profile = await fetchHeroProfile();
      setHeroProfile(profile);
      const result = await fetchTodaysBirthdays(profile, {
        simulateBirthday: !!user.dev_simulate_birthday || !!profile?.dev_simulate_birthday,
        displayName: getUserDisplayName(user),
      });
      setData(result);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const optimisticMe = useMemo((): HeroProfile | null => {
    if (data?.me) return data.me;
    if (heroProfile) return heroProfile;
    if (!user?.dev_simulate_birthday) return null;
    return {
      full_name: getUserDisplayName(user),
      dev_simulate_birthday: true,
      birth_date: devSimulatedBirthDateToday(),
    };
  }, [data?.me, heroProfile, user]);

  const mineIsBirthday =
    !!user?.dev_simulate_birthday ||
    isMyBirthdayToday(data?.me) ||
    isMyBirthdayToday(heroProfile) ||
    isMyBirthdayToday(optimisticMe);

  // Reihenfolge: Urlaub > Halloween (24.–31. Oktober) > Weihnachten.
  const halloweenSeason = isHalloweenSeason(user) && !vacationActive;
  const showHalloweenDecor = halloweenSeason && decoAllowsHalloweenFullDecor(decoPreference);
  const showHalloweenBats =
    halloweenSeason && decoAllowsHalloweenBats(decoPreference) && !mineIsBirthday;

  const christmasSeason = isChristmasSeason(user) && !vacationActive && !halloweenSeason;
  const showChristmasDecor = christmasSeason && decoAllowsChristmasDecor(decoPreference);
  const showChristmasSnow =
    christmasSeason && decoAllowsChristmasSnow(decoPreference) && !mineIsBirthday;

  useHalloweenEffects(showHalloweenDecor || showHalloweenBats, {
    showBats: showHalloweenBats,
    showSeasonDecor: showHalloweenDecor,
  });
  useChristmasEffects(showChristmasDecor || showChristmasSnow, {
    showSnow: showChristmasSnow,
    showSeasonDecor: showChristmasDecor,
  });
  useBirthdayConfetti(mineIsBirthday && decoAllowsBirthdayConfetti(decoPreference));

  const displayData = useMemo((): BirthdayData | null => {
    if (data) return data;
    if (mineIsBirthday && optimisticMe) {
      return { me: optimisticMe, todays: [] };
    }
    return null;
  }, [data, mineIsBirthday, optimisticMe]);

  const value = useMemo(
    () => ({
      data: displayData,
      mineIsBirthday,
      loading,
      reload,
    }),
    [displayData, mineIsBirthday, loading, reload],
  );

  return <BirthdayContext.Provider value={value}>{children}</BirthdayContext.Provider>;
}

export function useBirthday(): BirthdayContextValue {
  const ctx = useContext(BirthdayContext);
  if (!ctx) {
    return {
      data: null,
      mineIsBirthday: false,
      loading: false,
      reload: async () => {},
    };
  }
  return ctx;
}
